import React from 'react';
import { Navigate } from 'react-router-dom';
import { useAuth } from '../hooks/useAuth';
import { supabase } from '../supabaseClient';

interface ProtectedRouteProps {
  children: React.ReactNode;
  redirectTo?: string;
}

const ProtectedRoute: React.FC<ProtectedRouteProps> = ({ 
  children, 
  redirectTo = '/login' 
}) => {
  const { user, session, loading, isAuthenticated } = useAuth();
  const [hasDoubleChecked, setHasDoubleChecked] = React.useState(false);
  const [recheckSession, setRecheckSession] = React.useState<typeof session>(null);

  // Double-check session before redirecting to prevent false negatives
  // This helps avoid redirect loops when session is still being established
  // MUST be called before any conditional returns (React Hooks rule)
  React.useEffect(() => {
    if (!loading && !isAuthenticated && !hasDoubleChecked) {
      const doubleCheckSession = async () => {
        // Wait a bit for session to potentially be established
        await new Promise(resolve => setTimeout(resolve, 500));
        const { data: { session: checkedSession } } = await supabase.auth.getSession();
        setRecheckSession(checkedSession);
        setHasDoubleChecked(true);
      };
      doubleCheckSession();
    } else if (isAuthenticated) {
      setHasDoubleChecked(true);
      setRecheckSession(session);
    }
  }, [loading, isAuthenticated, hasDoubleChecked, session]);

  // Show loading state while checking authentication
  if (loading) {
    return (
      <div style={{ 
        color: '#D6FF3A', 
        background: '#18181a', 
        minHeight: '100vh', 
        display: 'flex', 
        alignItems: 'center', 
        justifyContent: 'center', 
        fontSize: '1.3rem' 
      }}>
        Checking authentication...
      </div>
    );
  }

  // Wait for double-check to complete before redirecting
  if (!loading && !isAuthenticated && !hasDoubleChecked) {
    return (
      <div style={{ 
        color: '#D6FF3A', 
        background: '#18181a', 
        minHeight: '100vh', 
        display: 'flex', 
        alignItems: 'center', 
        justifyContent: 'center', 
        fontSize: '1.3rem' 
      }}>
        Checking authentication...
      </div>
    );
  }

  // Redirect to login if not authenticated (after double-check)
  // Use recheckSession if available, otherwise use session from useAuth
  const finalSession = recheckSession || session;
  if (!loading && !isAuthenticated && !finalSession && hasDoubleChecked) {
    return <Navigate to={redirectTo} replace state={{ fromProtectedRoute: true }} />;
  }

  // Render the protected component if authenticated
  // Use recheckSession if available, otherwise use session from useAuth
  if (finalSession && (isAuthenticated || !!finalSession?.user)) {
    return <>{children}</>;
  }

  // Fallback: still checking
  return (
    <div style={{ 
      color: '#D6FF3A', 
      background: '#18181a', 
      minHeight: '100vh', 
      display: 'flex', 
      alignItems: 'center', 
      justifyContent: 'center', 
      fontSize: '1.3rem' 
    }}>
      Checking authentication...
    </div>
  );
};

export default ProtectedRoute;
